import type { CellType } from "./types";

/**
 * Function-argument-type catalog. For each Excel function, an ordered list of
 * the expected CellType per positional argument. If `variadic` is true, the
 * LAST entry repeats for every additional argument (e.g., SUM accepts an
 * arbitrary number of numeric args).
 *
 * Type semantics inside this catalog:
 *   - "number"  — any numeric (used when a function wants a numeric value
 *                 but doesn't care if it's a plain number, date, currency,
 *                 percent, etc.)
 *   - "date"    — function specifically expects a date serial (EOMONTH,
 *                 YEAR, DATEDIF, etc.). Stronger signal than "number".
 *   - "text"    — function specifically expects a text value.
 *   - "boolean" — function expects TRUE/FALSE.
 *   - "unknown" — function imposes no type constraint on this position
 *                 (IF condition, IFERROR fallback, lookup keys, etc.) —
 *                 NO signal will be emitted.
 *
 * Keys are uppercase Excel function names. Localized names are out of scope.
 */
export interface ArgSpec {
  types: CellType[];
  /** When true, the last `types` entry repeats for extra arguments. */
  variadic?: boolean;
}

export const FUNCTION_ARG_TYPES: Record<string, ArgSpec> = {
  // -------------------------------------------------------------- Date / time
  EOMONTH: { types: ["date", "number"] },
  EDATE: { types: ["date", "number"] },
  YEAR: { types: ["date"] },
  MONTH: { types: ["date"] },
  DAY: { types: ["date"] },
  HOUR: { types: ["datetime"] },
  MINUTE: { types: ["datetime"] },
  SECOND: { types: ["datetime"] },
  WEEKDAY: { types: ["date", "number"] },
  WEEKNUM: { types: ["date", "number"] },
  ISOWEEKNUM: { types: ["date"] },
  DAYS: { types: ["date", "date"] },
  DAYS360: { types: ["date", "date", "boolean"] },
  DATEDIF: { types: ["date", "date", "text"] },
  NETWORKDAYS: { types: ["date", "date", "date"], variadic: true },
  "NETWORKDAYS.INTL": { types: ["date", "date", "text", "date"], variadic: true },
  WORKDAY: { types: ["date", "number", "date"], variadic: true },
  "WORKDAY.INTL": { types: ["date", "number", "text", "date"], variadic: true },
  YEARFRAC: { types: ["date", "date", "number"] },
  DATE: { types: ["number", "number", "number"] },
  TIME: { types: ["number", "number", "number"] },

  // ------------------------------------------------------- Numeric / math agg
  SUM: { types: ["number"], variadic: true },
  SUMIF: { types: ["unknown", "unknown", "number"] },
  SUMIFS: { types: ["number", "unknown", "unknown"], variadic: true },
  SUMPRODUCT: { types: ["number"], variadic: true },
  SUMSQ: { types: ["number"], variadic: true },
  AVERAGE: { types: ["number"], variadic: true },
  AVERAGEA: { types: ["number"], variadic: true },
  AVERAGEIF: { types: ["unknown", "unknown", "number"] },
  AVERAGEIFS: { types: ["number", "unknown", "unknown"], variadic: true },
  MEDIAN: { types: ["number"], variadic: true },
  MODE: { types: ["number"], variadic: true },
  "MODE.SNGL": { types: ["number"], variadic: true },
  "MODE.MULT": { types: ["number"], variadic: true },
  MIN: { types: ["number"], variadic: true },
  MINA: { types: ["number"], variadic: true },
  MINIFS: { types: ["number", "unknown", "unknown"], variadic: true },
  MAX: { types: ["number"], variadic: true },
  MAXA: { types: ["number"], variadic: true },
  MAXIFS: { types: ["number", "unknown", "unknown"], variadic: true },
  PRODUCT: { types: ["number"], variadic: true },
  SUBTOTAL: { types: ["number", "number"], variadic: true },
  AGGREGATE: { types: ["number", "number", "number"], variadic: true },

  // ----------------------------------------------------- Numeric / math scalar
  ABS: { types: ["number"] },
  CEILING: { types: ["number", "number"] },
  "CEILING.MATH": { types: ["number", "number", "number"] },
  FLOOR: { types: ["number", "number"] },
  "FLOOR.MATH": { types: ["number", "number", "number"] },
  ROUND: { types: ["number", "number"] },
  ROUNDUP: { types: ["number", "number"] },
  ROUNDDOWN: { types: ["number", "number"] },
  MROUND: { types: ["number", "number"] },
  INT: { types: ["number"] },
  TRUNC: { types: ["number", "number"] },
  POWER: { types: ["number", "number"] },
  SQRT: { types: ["number"] },
  EXP: { types: ["number"] },
  LN: { types: ["number"] },
  LOG: { types: ["number", "number"] },
  LOG10: { types: ["number"] },
  MOD: { types: ["number", "number"] },
  QUOTIENT: { types: ["number", "number"] },
  SIGN: { types: ["number"] },
  RAND: { types: [] },
  RANDBETWEEN: { types: ["number", "number"] },

  // -------------------------------------------------------------- Statistical
  STDEV: { types: ["number"], variadic: true },
  "STDEV.S": { types: ["number"], variadic: true },
  "STDEV.P": { types: ["number"], variadic: true },
  STDEVA: { types: ["number"], variadic: true },
  STDEVP: { types: ["number"], variadic: true },
  VAR: { types: ["number"], variadic: true },
  "VAR.S": { types: ["number"], variadic: true },
  "VAR.P": { types: ["number"], variadic: true },
  VARA: { types: ["number"], variadic: true },
  VARP: { types: ["number"], variadic: true },
  VARPA: { types: ["number"], variadic: true },
  PERCENTILE: { types: ["number", "number"] },
  "PERCENTILE.INC": { types: ["number", "number"] },
  "PERCENTILE.EXC": { types: ["number", "number"] },
  QUARTILE: { types: ["number", "number"] },
  "QUARTILE.INC": { types: ["number", "number"] },
  "QUARTILE.EXC": { types: ["number", "number"] },
  PERCENTRANK: { types: ["number", "number", "number"] },
  RANK: { types: ["number", "number", "number"] },
  "RANK.EQ": { types: ["number", "number", "number"] },
  "RANK.AVG": { types: ["number", "number", "number"] },
  CORREL: { types: ["number", "number"] },
  COVAR: { types: ["number", "number"] },
  "COVARIANCE.P": { types: ["number", "number"] },
  "COVARIANCE.S": { types: ["number", "number"] },
  SLOPE: { types: ["number", "number"] },
  INTERCEPT: { types: ["number", "number"] },
  TREND: { types: ["number", "number", "number", "boolean"] },
  FORECAST: { types: ["number", "number", "number"] },
  "FORECAST.LINEAR": { types: ["number", "number", "number"] },
  GROWTH: { types: ["number", "number", "number", "boolean"] },
  LARGE: { types: ["number", "number"] },
  SMALL: { types: ["number", "number"] },
  GEOMEAN: { types: ["number"], variadic: true },
  HARMEAN: { types: ["number"], variadic: true },

  // --------------------------------------------------------------- Counting
  COUNT: { types: ["unknown"], variadic: true },
  COUNTA: { types: ["unknown"], variadic: true },
  COUNTBLANK: { types: ["unknown"] },
  COUNTIF: { types: ["unknown", "unknown"] },
  COUNTIFS: { types: ["unknown", "unknown"], variadic: true },

  // ---------------------------------------------------------------- Financial
  PMT: { types: ["number", "number", "number", "number", "number"] },
  PV: { types: ["number", "number", "number", "number", "number"] },
  FV: { types: ["number", "number", "number", "number", "number"] },
  IPMT: { types: ["number", "number", "number", "number", "number", "number"] },
  PPMT: { types: ["number", "number", "number", "number", "number", "number"] },
  CUMIPMT: { types: ["number", "number", "number", "number", "number", "number"] },
  CUMPRINC: { types: ["number", "number", "number", "number", "number", "number"] },
  NPV: { types: ["number"], variadic: true },
  IRR: { types: ["number", "number"] },
  MIRR: { types: ["number", "number", "number"] },
  RATE: { types: ["number", "number", "number", "number", "number", "number"] },
  NPER: { types: ["number", "number", "number", "number", "number"] },
  XNPV: { types: ["number", "number", "date"] },
  XIRR: { types: ["number", "date", "number"] },
  EFFECT: { types: ["number", "number"] },
  NOMINAL: { types: ["number", "number"] },
  SLN: { types: ["number", "number", "number"] },
  DB: { types: ["number", "number", "number", "number", "number"] },
  DDB: { types: ["number", "number", "number", "number", "number"] },
  SYD: { types: ["number", "number", "number", "number"] },

  // ----------------------------------------------------------------- Text
  LEN: { types: ["text"] },
  LEFT: { types: ["text", "number"] },
  RIGHT: { types: ["text", "number"] },
  MID: { types: ["text", "number", "number"] },
  TRIM: { types: ["text"] },
  UPPER: { types: ["text"] },
  LOWER: { types: ["text"] },
  PROPER: { types: ["text"] },
  CONCATENATE: { types: ["text"], variadic: true },
  CONCAT: { types: ["text"], variadic: true },
  TEXTJOIN: { types: ["text", "boolean", "text"], variadic: true },
  SUBSTITUTE: { types: ["text", "text", "text", "number"] },
  REPLACE: { types: ["text", "number", "number", "text"] },
  FIND: { types: ["text", "text", "number"] },
  SEARCH: { types: ["text", "text", "number"] },
  REPT: { types: ["text", "number"] },
  EXACT: { types: ["text", "text"] },
  TEXT: { types: ["number", "text"] }, // first arg is the value being formatted
  VALUE: { types: ["text"] },
  NUMBERVALUE: { types: ["text", "text", "text"] },
  DATEVALUE: { types: ["text"] },
  TIMEVALUE: { types: ["text"] },
  CLEAN: { types: ["text"] },
  CHAR: { types: ["number"] },
  CODE: { types: ["text"] },
  UNICHAR: { types: ["number"] },
  UNICODE: { types: ["text"] },
  DOLLAR: { types: ["number", "number"] },
  FIXED: { types: ["number", "number", "boolean"] },
  TEXTBEFORE: { types: ["text", "text", "number", "boolean", "boolean", "text"] },
  TEXTAFTER: { types: ["text", "text", "number", "boolean", "boolean", "text"] },
  TEXTSPLIT: { types: ["text", "text", "text", "boolean", "boolean", "text"] },

  // -------------------------------------------------------------- Logical
  IF: { types: ["unknown", "unknown", "unknown"] },
  IFS: { types: ["unknown"], variadic: true },
  AND: { types: ["boolean"], variadic: true },
  OR: { types: ["boolean"], variadic: true },
  XOR: { types: ["boolean"], variadic: true },
  NOT: { types: ["boolean"] },
  IFERROR: { types: ["unknown", "unknown"] },
  IFNA: { types: ["unknown", "unknown"] },
  SWITCH: { types: ["unknown"], variadic: true },

  // ----------------------------------------------------- Lookup / reference
  VLOOKUP: { types: ["unknown", "unknown", "number", "boolean"] },
  HLOOKUP: { types: ["unknown", "unknown", "number", "boolean"] },
  XLOOKUP: {
    types: ["unknown", "unknown", "unknown", "unknown", "number", "number"],
  },
  LOOKUP: { types: ["unknown", "unknown", "unknown"] },
  MATCH: { types: ["unknown", "unknown", "number"] },
  XMATCH: { types: ["unknown", "unknown", "number", "number"] },
  INDEX: { types: ["unknown", "number", "number"] },
  CHOOSE: { types: ["number"], variadic: true },
  OFFSET: { types: ["unknown", "number", "number", "number", "number"] },
  INDIRECT: { types: ["text", "boolean"] },
  ADDRESS: { types: ["number", "number", "number", "boolean", "text"] },
  ROW: { types: ["unknown"] },
  COLUMN: { types: ["unknown"] },
  ROWS: { types: ["unknown"] },
  COLUMNS: { types: ["unknown"] },
  HYPERLINK: { types: ["text", "text"] },

  // ----------------------------------------------------------- Information
  ISBLANK: { types: ["unknown"] },
  ISNUMBER: { types: ["unknown"] },
  ISTEXT: { types: ["unknown"] },
  ISNONTEXT: { types: ["unknown"] },
  ISERROR: { types: ["unknown"] },
  ISERR: { types: ["unknown"] },
  ISNA: { types: ["unknown"] },
  ISLOGICAL: { types: ["unknown"] },
  ISEVEN: { types: ["number"] },
  ISODD: { types: ["number"] },
  N: { types: ["unknown"] },
  T: { types: ["unknown"] },
  TYPE: { types: ["unknown"] },
  CELL: { types: ["text", "unknown"] },

  // ----------------------------------------------- Trig (all numeric scalars)
  SIN: { types: ["number"] },
  COS: { types: ["number"] },
  TAN: { types: ["number"] },
  ASIN: { types: ["number"] },
  ACOS: { types: ["number"] },
  ATAN: { types: ["number"] },
  ATAN2: { types: ["number", "number"] },
  SINH: { types: ["number"] },
  COSH: { types: ["number"] },
  TANH: { types: ["number"] },
  DEGREES: { types: ["number"] },
  RADIANS: { types: ["number"] },
  PI: { types: [] },
};

/**
 * Map form of the catalog — used at runtime, populated either from the
 * built-in `FUNCTION_ARG_TYPES` record or from user customizations loaded
 * via `src/celltypes/storage.ts`.
 */
export type CatalogMap = Map<string, ArgSpec>;

/** Single-entry shape used when serializing the catalog to localStorage. */
export interface FunctionCatalogEntry {
  /** Uppercase Excel function name. */
  name: string;
  /** Argument types in order. */
  types: CellType[];
  /** When true, the last `types` entry repeats for extra arguments. */
  variadic: boolean;
}

/** Alias retained for backward-compat with code that imported the record. */
export const DEFAULT_ARG_TYPES = FUNCTION_ARG_TYPES;

/** Convert the built-in record to a Map for runtime lookup. */
export function defaultCatalogAsMap(): CatalogMap {
  return new Map(Object.entries(FUNCTION_ARG_TYPES));
}

/** Convert the built-in record to entry array form for storage/UI. */
export function defaultCatalogAsEntries(): FunctionCatalogEntry[] {
  return Object.entries(FUNCTION_ARG_TYPES).map(([name, spec]) => ({
    name,
    types: spec.types,
    variadic: !!spec.variadic,
  }));
}

/**
 * Look up the expected type for the Nth argument of `funcName` (0-based).
 * Returns null if the function isn't catalogued, or "unknown" if catalogued
 * but the position has no type constraint.
 *
 * If a `catalog` Map is provided, it OVERRIDES the built-in record. A
 * function present in `catalog` but absent from the built-in works fine;
 * a function in the built-in but absent from `catalog` is treated as
 * uncatalogued (since the user explicitly removed it).
 */
export function expectedTypeAt(
  funcName: string,
  argIndex: number,
  catalog?: CatalogMap
): CellType | null {
  const upper = funcName.toUpperCase();
  const spec = catalog ? catalog.get(upper) : FUNCTION_ARG_TYPES[upper];
  if (!spec) return null;
  if (spec.types.length === 0) return null;
  if (argIndex < spec.types.length) return spec.types[argIndex];
  if (spec.variadic) return spec.types[spec.types.length - 1];
  return null;
}
